import { Component, Input, Output, OnChanges, SimpleChanges, EventEmitter, HostListener } from '@angular/core';
import { SearchbarCustomEvent, Platform, ModalController} from '@ionic/angular';

@Component({
    selector: 'app-search-select',
    templateUrl: './search-select.component.html',
    styleUrls: ['./search-select.component.scss'],
    standalone: false
})
export class SearchSelectComponent implements OnChanges {

  private  _title : string = 'Search';
  private  _data: any[];
  private  _multiple = false;
  private  _itemTextField = 'name';
  private  _defaultSelect = false;
  private  _selectedIDs: any[];
  private  _showInactive = false;
  private  _inactiveSelectable = false;
  private  _inactiveCounter : number = 0;
  private  _capitalize = false;

  @Output() selectedChange: EventEmitter<any> = new EventEmitter()

  @Input() set title(value: string) {
    this._title = value;
  }

  @Input() set data(value: any[]) {
    this._data = value;
  }

  @Input() set multiple(value: boolean) {
    this._multiple = value;
  }

  @Input() set itemTextField(value: string) {
    this._itemTextField = value;
  }

  @Input() set defaultSelect(value: boolean) {
    this._defaultSelect = value;
  }

  @Input() set selectedIDs(value: any[]) {
    this._selectedIDs = value;
  }

  @Input() set showInactive(value: boolean){
    this._showInactive = value;
  }

  @Input() set inactiveSelectable(value: boolean){
    this._inactiveSelectable = value;
  }

  @Input() set capitalize(value: boolean) {
    this._capitalize = value;
  }

  isOpen        = false;
  selected      = [];
  filtered      = [];
  searchText    = '';
  clearString: String = 'Clear';
  selectAllString: String = 'Select All'
  buttonName: String = this.clearString;

  constructor(private modalController: ModalController) {}

  ngOnChanges(changes: SimpleChanges) : void {
    this.selected = [];
    this.initSelected(this._selectedIDs);

    // if no data is provided to component and "defaultSelect" is true
    // all the data is selected by default
    if (this._defaultSelect && this._data && (this._selectedIDs === undefined || this._selectedIDs === null)){
      if (this.selected){
        for (let item of this._data) {
          item.selected = this.selected.some(sel => sel.id === item.id);
        }
      }
      for (let item of this._data){
        item.selected = true;
      }
      this.selected = this._data;
    }
    this.filtered = this._data;
  }

  ngOnInit() : void {
    const modalState = {
      modal : true,
      desc : ''
    };
    history.pushState(modalState, null);

    // Listen for the Escape key press
    document.addEventListener('keydown', (event: KeyboardEvent) => this.handleKeyDown(event));
    if (this._selectedIDs !== undefined && (this.selected === undefined || this.selected.length == 0)) {
      this.selected = [];
      this.initSelected(this._selectedIDs);
    }
  }

  ngOnDestroy() {
    if (window.history.state.modal) {
      history.back();
    }
  }

  @HostListener('window:popstate')
  dismissModal() {
    if(this.modalController && this.isOpen){
      this.modalController.dismiss();
      this.isOpen = false;
    }
  }

  private initSelected(selectedID) : void {
    // check if selectedIDs is single value or array then implement logic to set selected
    try{
      if (selectedID !== null ){ // && selectedID !== undefined) {
        if (typeof selectedID === 'string') {
          this.selectData(selectedID);
        } else if (typeof selectedID === 'number') {
          this.selectData(`${selectedID}`);
        } else if (Array.isArray(selectedID)){
          for (let item of selectedID){
            //if(item !== null && item !== undefined){
              this.initSelected(item['id']);
            //}
          }
        } else if (typeof selectedID === 'object' && Object.entries(selectedID).length > 0) {
          for (let item of Object.entries(selectedID)){
            //if(item !== null && item !== undefined){
              this.initSelected(item['id']);
            //}
          }
        }
      }
    } catch (e:any){
      console.log('error when initializing the component values: ' + e.Message);
    }

  }

  private selectData(itemID : string) : void {
    //loop over data and add items to selected
    const selectedDatum = this._data.find(datum => datum.id == itemID);
    // If the item is found, add it to the selected array and mark it as selected
    if (selectedDatum) {
      this.selected.push(selectedDatum);
    }
  }

  /**
   * Sets the items to be displayed in component.
   * Inactive items should only be displayed if they are preselected.
   * e.g. Items that have been set to inactive, but are refrenced by an active data set.
   */
  private setFiltered() : void{
    this._inactiveCounter = 0;
    if (this.filtered && this.filtered.length) {
      this.filtered = this.filtered.filter(item => {
        // If item is not active, check further conditions
        if (item.active === 0 &&!this._showInactive) {
          // If selectedIDs is undefined, exclude the item
          if (this.selected === undefined) {
            return false;
          }
          // If the item is not selected, exclude it
          if (!this.selected.some(sel => sel.id === item.id)) {
            return false;
          }
        }
        if (item.active === 0) {
          this._inactiveCounter = this._inactiveCounter +1;
        }
        // Keep the item in the filtered list
        return true;
      });
      //set selected items for the remaining items
      this.filtered.forEach(item => item.selected = this.selected.some(sel => sel.id === item.id));

      // Sort the filtered array by active status
      this.filtered.sort((a, b) => b.active - a.active);
    }
  }

  open() : void {
    this.isOpen = true;
    this.searchText = '';
    if (this._data){
      this.filtered = this._data;
      this.setFiltered();
      this.initButtonName();
    }
  }

  initButtonName(){
    if(this.selected.length>0){
      this.buttonName = this.clearString;
    }else{
      this.buttonName = this.selectAllString;
    }
  }

  clearOrSelect() : void {
    this.searchText = '';
    if (this.filtered && this.selected !== undefined && this.selected.length > 0 && this.buttonName==this.clearString) {
      this.filtered.forEach(item => item.selected = false);
      this.buttonName = this.selectAllString;
    } else {
      this.filtered.forEach(item => item.selected = true);
      this.buttonName = this.clearString;
    }
  }

  cancel() : void {
      this.isOpen = false;
      this.selected.forEach(item => item.selected = true);
      this.setFiltered();
      this.updateButton();
  }

  private updateButton(){
    this.buttonName = this.selected && this.selected.length > 0 ? this.clearString : this.selectAllString;
  }

  select(closeDialog : boolean = true) : void {
    const selected = this._data.filter((item) => item.selected);
    this.selected = selected;
    this.selectedChange.emit(selected);
    this.isOpen = !closeDialog;
  }

  itemSelected(item) : void {
    if(!this._multiple) {
      if (this.selected.length){
        this.selected[0].selected = false;
      }
      this.selected = this._data.filter((item) => item.selected);
      if (this.selected){
        this.buttonName=this.clearString;
        this.selectedChange.emit(this.selected);
      }
      this.isOpen = false;
    }
  }

  filter(event: SearchbarCustomEvent) {
    const filter = this.searchText.toLowerCase();
    this.filtered = this._data.filter((item) => this.leaf(item).toLowerCase().indexOf(filter) >=0);
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.cancel();
    }
  }

  filterClear(){
    this.searchText='';
    this.filter(null);
  }

  leaf(obj) {
    var text = '';
    //to handle multiple fields like name and surname separated by "&"
    if (this._itemTextField.indexOf('&')>0){
        for (var field of this._itemTextField.split('&')){
          text = (text != '' ? text + ' ':'') + field.split('.').reduce((value, el) => value[el], obj);
        }
    } else {
      text = this._itemTextField.split('.').reduce((value, el) => value[el], obj);
    }
    return text;
  }

  getInactiveCounter() : number{
    return this._inactiveCounter;
  }

  isInactiveSelectable():boolean{
    return this._inactiveSelectable;
  }

  isMultiple() : boolean{
    return this._multiple;
  }

  shouldCapitalize():boolean{
    return this._capitalize;
  }

  selectedItemsString(): string {
     return this.selected.map(item => this.leaf(item)).join(', ')
  }

  getTitle(): string {
    return this._title;
  }
}
